import React from 'react';
import classnames from 'classnames/bind';
import { Menu, MenuItem, MenuItemProps } from '@mantine/core';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  ChartType,
  DataWidget,
  FilesSearchWidget,
  FilesWidget,
  IframeWidget,
  MultiStreamWidget,
  StreamWidget,
} from 'types/dashboard_widget';
import { DashboardParams } from 'dashboards/types/dashboard_params';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useModal } from 'hooks/useModal';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { Heading } from 'components/Heading/Heading';

import { IconButton } from 'components/IconButton/IconButton';
import { Icon } from 'components/Icon/Icon';

import styles from './Widget.module.scss';

const c = classnames.bind(styles);

type AdditionalMenuItems = Record<
  string,
  MenuItemProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>;

type WidgetProps = React.PropsWithChildren<{
  widget:
    | DataWidget<ChartType>
    | FilesWidget
    | FilesSearchWidget
    | StreamWidget
    | MultiStreamWidget
    | IframeWidget;
  menuItems?: AdditionalMenuItems;
}>;

// Wrapper component for dashboard widgets that handles heading and context menu
export function Widget({ widget, menuItems = {}, children }: WidgetProps) {
  const { dashboardID: dashboardId } = useParams<DashboardParams>();

  const { open } = useModal();

  const [searchParams] = useSearchParams();
  const hideControls = searchParams.get('hideControls') === 'true';

  const { data: dashboard } = useDashboard(dashboardId);

  const { mutate: updateDashboard } = useUpdateDashboard();

  if (hideControls) {
    return null;
  }

  function handleDeleteWidget() {
    if (!dashboard) {
      return;
    }

    const updatedWidgets = dashboard.configuration.widgets.filter(
      ({ id }) => widget.id !== id
    );

    updateDashboard({
      id: dashboard.id,
      configuration: { widgets: updatedWidgets },
    });
  }

  function handleDuplicateWidget() {
    if (!dashboard) {
      return;
    }

    const widgetToBeDuplicated = dashboard.configuration.widgets.find(
      ({ id }) => widget.id === id
    );

    if (!widgetToBeDuplicated) {
      return;
    }

    // Get new layout (xy coordinates) for duplicated widget
    const newLayout = getNewWidgetLayout(
      dashboard.configuration.widgets,
      widgetToBeDuplicated.layout.w,
      widgetToBeDuplicated.layout.h
    );

    // Duplicate widget with new id, layout and name
    const duplicatedWidget = structuredClone(widgetToBeDuplicated);
    const newId = crypto.randomUUID();
    duplicatedWidget.id = newId;
    duplicatedWidget.name = `Copy of ${duplicatedWidget.name}`;
    duplicatedWidget.layout = { ...newLayout, i: newId };

    const updatedDashboard = upsertWidget(dashboard, duplicatedWidget);

    updateDashboard({
      id: dashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  function handleEditWidget() {
    if (!dashboardId) {
      return;
    }

    open('edit-widget', { widget, dashboardId });
  }

  return (
    <div className={c('wrap')}>
      <div className={c('header')}>
        <Heading level={3}>{widget.name}</Heading>
        <Menu>
          <Menu.Target>
            <IconButton
              size="small"
              icon="more-vertical"
              label="Widget options"
            />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              onClick={handleEditWidget}
              leftSection={<Icon name="edit" size="small" />}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              onClick={handleDuplicateWidget}
              leftSection={<Icon name="copy" size="small" />}
            >
              Duplicate
            </Menu.Item>

            {Object.entries(menuItems).map(([key, props]) => (
              <MenuItem key={key} {...props} />
            ))}

            <Menu.Divider />

            <Menu.Item
              color="red"
              onClick={handleDeleteWidget}
              leftSection={<Icon name="delete" size="small" unstyled />}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      {children}
    </div>
  );
}
